import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import API_BASE_URL from "../../config";
import DynamicContactsTable from "./DynamicContactsTable";
import useColumnPreferences from "../../hooks/useColumnPreferences";
import PaginationControls from "./PaginationControls";
import FilterBuilder from "../common/FilterBuilder";
import type { ViewEditorPayload } from "../common/FilterBuilder";
import AppModal from "../common/AppModal";
import WebsiteGlobeIcon from "../common/WebsiteGlobeIcon";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import duplicateIcon from "../../assets/images/icons/duplicate.png";
import BulkUpdatePanel from "./BulkUpdatePanel";
import SegmentModal from "../common/SegmentModal";
import { formatIsoDate, formatUserDate } from "../common/dateTimePreferences";
import ToastMessage from "../common/ToastMessage";

import {
  faEdit,
  faTrashAlt,
} from "@fortawesome/free-regular-svg-icons";

import type {
  FieldType,
  FilterCondition,
  FilterGroup,
  JoinOperator,
} from "../common/filterTypes";
import {
  TRACKING_CLICK_FIELD,
  TRACKING_OPEN_FIELD,
  buildTrackingIndexesForGroups,
  conditionRequiresCampaign,
  evaluateTrackingCondition,
  hasRequiredConditionContext,
} from "../../utils/trackingFilterUtils";

import { useAppModal } from "../../hooks/useAppModal";
import { useAppData } from "../../contexts/AppDataContext";
import { useDispatch, useSelector } from "react-redux";
import { ContactsToolbar, ContactsListsRows } from "./ContactList.new";
import { RootState } from "../../Redux/store";
import { closePanel, openPanel } from "../../slices/panelSlice";
import ValidationRunPanel from "./validation/ValidationRunPanel";
import {
  isRelativeDateToken,
  matchesDayCondition,
} from "../common/relativeDateFilters";
import {
  VALIDATION_COLUMN_LABELS,
  VALIDATION_EXCLUDED_FIELDS,
  createValidationFormatters,
  getValidationScoreColumn,
  verifiedScore,
  getValidationFieldValue,
} from "./validation/validationColumns";
import {
  MANUAL_SCORES_ROW_KEY,
  SUGGESTION_ROW_KEYS,
  VALIDATION_SCORE_KEYS,
  withManualScoreMark,
  type AppliedSuggestion,
  type ValidationCheckType,
  type ValidationJob,
  type ValidationSuggestion,
} from "../../api/contactValidation";
import { useToast } from "../../hooks/useToast";


interface ContactFieldOption {
  key: string;
  label: string;
  type: string;
  options?: string[];
  contextType?: "campaign";
}

interface ViewSummary {
  id: number;
  name: string;
  description?: string;
  created_at?: string;
  contactCount?: number;
}

interface ViewMeta {
  filtersJson?: string;
  dataFileIds?: number[];
  segmentIds?: number[];
  useAllDataFiles?: boolean;
  excludedDataFileIds?: number[];
}

interface ViewItem extends ViewSummary, ViewMeta {}

interface ContactViewsProps {
  clientId: string | number;
  isAdmin?: boolean;
  filterFields: ContactFieldOption[];
  columnNameMap?: Record<string, string>;
  customFieldIdByName?: Record<string, number>;
  defaultVisibleColumns?: string[];
  onShowMessage?: (message: string, type: "success" | "error") => void;
  isActive?: boolean;
  refreshToken?: number;
  currentTab?: string;
  onViewModeChange?: (mode: "list" | "detail") => void;
}

const ViewContactProfileAvatar: React.FC<{
  contact: any;
  clientId: string | number;
  onOpen: (contact: any) => void;
}> = ({ contact, clientId, onOpen }) => {
  const [imageUnavailable, setImageUnavailable] = useState(false);
  const firstName = String(contact.first_name || contact.firstName || "").trim();
  const lastName = String(contact.last_name || contact.lastName || "").trim();
  const displayName = `${firstName} ${lastName}`.trim() || contact.full_name || contact.fullName || contact.email || "Contact";
  const initials = `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase() || String(displayName).slice(0, 2).toUpperCase();
  const imageUrl = `${API_BASE_URL}/api/Attachment/profile-image/${contact.id}?clientId=${clientId}`;

  return (
    <button
      type="button"
      title={`Open ${displayName} profile`}
      onClick={(event) => {
        event.stopPropagation();
        onOpen(contact);
      }}
      style={{ width: 34, height: 34, padding: 0, border: "1px solid #dfe7df", borderRadius: "50%", overflow: "hidden", background: "#eef8ef", color: "#3f9f42", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, cursor: "pointer" }}
    >
      {!imageUnavailable ? (
        <img
          src={imageUrl}
          alt={`${displayName} profile`}
          loading="lazy"
          onError={() => setImageUnavailable(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : initials || "?"}
    </button>
  );
};

interface FilterBuilderFieldOption {
  key: string;
  label: string;
  type: FieldType;
  options?: string[];
  contextType?: "campaign";
}

type CsvColumn = {
  key: string;
  header: string;
  getValue?: (contact: any) => any;
};

const VIEW_META_PREFIX = "crm_view_meta_";
const VIEW_STATE_PREFIX = "crm_view_state_";

const getViewMetaKey = (clientId: string | number) =>
  `${VIEW_META_PREFIX}${clientId}`;

const getViewStateKey = (clientId: string | number) =>
  `${VIEW_STATE_PREFIX}${clientId}`;

const loadViewMetaMap = (clientId: string | number): Record<string, ViewMeta> => {
  try {
    const raw = localStorage.getItem(getViewMetaKey(clientId));
    return raw ? JSON.parse(raw) : {};
  } catch (error) {
    console.warn("Failed to load view metadata:", error);
    return {};
  }
};

const saveViewMetaMap = (clientId: string | number, map: Record<string, ViewMeta>) => {
  try {
    localStorage.setItem(getViewMetaKey(clientId), JSON.stringify(map));
  } catch (error) {
    console.warn("Failed to save view metadata:", error);
  }
};

const loadViewState = (
  clientId: string | number
): { viewId: number; viewMode: "detail"; source?: string; nonce?: number } | null => {
  try {
    const raw = sessionStorage.getItem(getViewStateKey(clientId));
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    if (parsed?.viewMode !== "detail" || !Number.isFinite(Number(parsed?.viewId))) {
      return null;
    }

    return {
      viewId: Number(parsed.viewId),
      viewMode: "detail",
      source: typeof parsed.source === "string" ? parsed.source : undefined,
      nonce: Number.isFinite(Number(parsed.nonce)) ? Number(parsed.nonce) : undefined,
    };
  } catch (error) {
    console.warn("Failed to load view state:", error);
    return null;
  }
};

const saveViewState = (
  clientId: string | number,
  state: { viewId: number; viewMode: "detail" }
) => {
  try {
    sessionStorage.setItem(getViewStateKey(clientId), JSON.stringify(state));
  } catch (error) {
    console.warn("Failed to save view state:", error);
  }
};

const clearViewState = (clientId: string | number) => {
  try {
    sessionStorage.removeItem(getViewStateKey(clientId));
  } catch (error) {
    console.warn("Failed to clear view state:", error);
  }
};

const getFirstDefined = <T,>(...values: Array<T | undefined | null>): T | undefined =>
  values.find((value) => value !== undefined && value !== null);

const normalizeFiltersJson = (value: any): string => {
  if (!value) {
    return "";
  }

  if (typeof value === "string") {
    return value;
  }

  try {
    return JSON.stringify(value);
  } catch {
    return "";
  }
};

const normalizeIdArray = (value: any): number[] => {
  if (Array.isArray(value)) {
    return value.map(Number).filter((id) => Number.isFinite(id));
  }

  if (value == null || value === "") {
    return [];
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
      try {
        return normalizeIdArray(JSON.parse(trimmed));
      } catch {
        return [];
      }
    }

    return trimmed
      .split(",")
      .map((entry) => Number(entry.trim()))
      .filter((id) => Number.isFinite(id));
  }

  const id = Number(value);
  return Number.isFinite(id) ? [id] : [];
};

const normalizeFieldType = (value?: string): FieldType => {
  switch ((value || "").toLowerCase()) {
    case "number":
      return "number";
    case "date":
    case "datetime":
      return "date";
    case "boolean":
      return "boolean";
    case "dropdown":
      return "dropdown";
    case "longtext":
    case "text":
    default:
      return "text";
  }
};

const normalizeCustomFieldKey = (value: string) =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

const getRowValue = (row: Record<string, any>, fieldKey: string) => {
  const directValue = row[fieldKey];

  if (directValue !== undefined) {
    return directValue;
  }

  const validationValue = getValidationFieldValue(row, fieldKey);
  if (validationValue !== undefined) {
    return validationValue;
  }

  if (!fieldKey.startsWith("custom_")) {
    return directValue;
  }

  const customKey = fieldKey.replace(/^custom_/, "");
  const customFields =
    row.customFields && typeof row.customFields === "object"
      ? row.customFields
      : {};

  if (customKey in customFields) {
    return customFields[customKey];
  }

  const normalizedTarget = normalizeCustomFieldKey(customKey);
  const matchedEntry = Object.entries(customFields).find(
    ([key]) => normalizeCustomFieldKey(key) === normalizedTarget
  );

  if (matchedEntry) {
    return matchedEntry[1];
  }

  if (customKey in row) {
    return row[customKey];
  }

  return directValue;
};

const isCompleteCondition = (condition: FilterCondition) =>
  condition.field?.trim() &&
  condition.operator?.trim() &&
  (condition.operator === "isEmpty" ||
    condition.operator === "isNotEmpty" ||
    String(condition.value ?? "").trim() !== "") &&
  hasRequiredConditionContext(condition);

interface SourceOption {
  id: number;
  name: string;
}

interface ParsedFilterGroup {
  id?: string;
  joinWithPrevious?: JoinOperator;
  conditions: FilterCondition[];
}

const parseFiltersJson = (
  filtersJson?: string
): { logic: string; groups: ParsedFilterGroup[] } => {
  if (!filtersJson) {
    return { logic: "NONE", groups: [] };
  }

  try {
    const parsed = JSON.parse(filtersJson);
    if (!parsed) {
      return { logic: "NONE", groups: [] };
    }
    if (Array.isArray(parsed.groups)) {
      return { logic: String(parsed.logic || "GROUPS"), groups: parsed.groups };
    }
    if (Array.isArray(parsed.conditions)) {
      return {
        logic: String(parsed.logic || "CHAIN"),
        groups: [{ conditions: parsed.conditions as FilterCondition[] }],
      };
    }
    return {
      logic: "NONE",
      groups: [],
    };
  } catch {
    return { logic: "NONE", groups: [] };
  }
};

const formatDate = formatUserDate;

const getContactNameParts = (row: any) => {
  const first =
    row?.first_name?.trim() ||
    row?.firstName?.trim() ||
    "";
  const last =
    row?.last_name?.trim() ||
    row?.lastName?.trim() ||
    "";
  let full =
    row?.full_name?.trim() ||
    row?.fullName?.trim() ||
    "";

  if (!full && (first || last)) {
    full = `${first} ${last}`.trim();
  }

  if (!first && !last && full) {
    const parts = full.split(" ").filter(Boolean);
    return {
      firstName: parts[0] || "",
      lastName: parts.slice(1).join(" ").trim(),
      fullName: full,
    };
  }

  return { firstName: first, lastName: last, fullName: full };
};

const getDisplayName = (row: any) => {
  const { fullName } = getContactNameParts(row);
  return fullName || row?.email || "-";
};

const getCustomFieldValue = (contact: any, fieldName: string) => {
  const directKey = `custom_${normalizeCustomFieldKey(fieldName)}`;
  if (contact?.[directKey] !== undefined) {
    return contact[directKey];
  }

  let customFieldsValue = contact?.customFields;
  if (typeof customFieldsValue === "string") {
    try {
      customFieldsValue = JSON.parse(customFieldsValue);
    } catch {
      customFieldsValue = undefined;
    }
  }

  if (customFieldsValue && typeof customFieldsValue === "object") {
    if (fieldName in customFieldsValue) return customFieldsValue[fieldName];
    const normalizedTarget = normalizeCustomFieldKey(fieldName);
    const matchedEntry = Object.entries(customFieldsValue).find(
      ([key]) => normalizeCustomFieldKey(key) === normalizedTarget
    );
    if (matchedEntry) return matchedEntry[1];
  }

  return "";
};

const getTrackingCampaignIds = (groups: FilterGroup[]) =>
  Array.from(
    new Set(
      groups.flatMap((group) =>
        (group.conditions || [])
          .filter((condition) => conditionRequiresCampaign(condition))
          .map((condition) => Number(condition.context?.campaignId))
          .filter((campaignId) => Number.isFinite(campaignId) && campaignId > 0)
      )
    )
  );

const ContactViews: React.FC<ContactViewsProps> = ({
  clientId,
  isAdmin = false,
  filterFields,
  columnNameMap,
  customFieldIdByName,
  defaultVisibleColumns,
  isActive = false,
  refreshToken = 0,
  currentTab,
  onViewModeChange,
}) => {
  const dispatch = useDispatch();
  const [views, setViews] = useState<ViewItem[]>([]);
  const [viewSearch, setViewSearch] = useState("");
  const [currentPageViews, setCurrentPageViews] = useState(1);
  const [pageSizeViews, setPageSizeViews] = useState<number | "All">(30);
  const [viewSortKey, setViewSortKey] = useState("");
  const [viewSortDirection, setViewSortDirection] = useState<"asc" | "desc">(
    "asc"
  );
  const [isLoadingViews, setIsLoadingViews] = useState(false);
  const [availableDataFiles, setAvailableDataFiles] = useState<SourceOption[]>([]);
  const [availableSegments, setAvailableSegments] = useState<SourceOption[]>([]);
  const [isLoadingSources, setIsLoadingSources] = useState(false);

  const [viewMode, setViewMode] = useState<"list" | "detail">("list");
  const [selectedView, setSelectedView] = useState<ViewItem | null>(null);
  const [baseViewContacts, setBaseViewContacts] = useState<any[]>([]);
  const [viewContacts, setViewContacts] = useState<any[]>([]);
  /** Per-view totals the pager prefers over the loaded row count. */
  const [viewContactCounts, setViewContactCounts] = useState<Record<number, number>>({});
  /**
   * The filter the editor's "Apply filters" built but the view has not saved.
   * Non-null means the server filters by this instead of the view's saved
   * filters, so the preview covers every contact in the view - the rows the
   * browser holds are one page of it, and filtering those locally hid matches
   * sitting on the other pages.
   */
  const [draftFiltersJson, setDraftFiltersJson] = useState<string | null>(null);
  /** How many rows the applied draft matches, for the pager. */
  const [draftFilteredTotal, setDraftFilteredTotal] = useState<number | null>(null);

  /**
   * Writes the outcome of an accepted or dismissed Data Integrity suggestion
   * into the rows already on screen, instead of reloading the view.
   *
   * The filtered list and the list the filter reads from are both patched: the
   * grid shows the first, and leaving the second stale would bring the old
   * value back the moment a filter was re-applied.
   */
  const applySuggestionOutcome = useCallback(
    (
      contactId: number,
      checkType: ValidationCheckType,
      suggestions: ValidationSuggestion[],
      applied?: AppliedSuggestion | null
    ) => {
      const suggestionsJson = JSON.stringify(suggestions);
      // Which of the four blobs on the row this list belongs to.
      const rowKey = SUGGESTION_ROW_KEYS[checkType];

      const patchRow = (row: any) => {
        if (!row || Number(row.id) !== contactId) return row;

        const next: any = {
          ...row,
          validation: {
            ...(row.validation ?? {}),
            [rowKey]: suggestionsJson,
          },
        };

        // Absent on a dismissal: nothing was written to the contact.
        if (applied) {
          next[applied.field] = applied.value;

          // A name correction rebuilds first and last as well, and those are
          // the columns the grid actually shows.
          if (applied.field === "full_name") {
            next.full_name = applied.fullName ?? applied.value;
            next.first_name = applied.firstName ?? next.first_name;
            next.last_name = applied.lastName ?? next.last_name;
          }
        }

        return next;
      };

      setViewContacts((prev) => prev.map(patchRow));
      setBaseViewContacts((prev) => prev.map(patchRow));
    },
    []
  );

  /**
   * Raises one check's score to 100 on the rows on screen.
   *
   * Only that score moves. The other three checks keep their own numbers,
   * because a user overruling one verdict has not thereby confirmed the other
   * three — the whole-contact "Mark as verified" is the action that speaks for
   * all of them.
   */
  const applyScoreVerified = useCallback(
    (contactId: number, checkType: ValidationCheckType) => {
      const scoreKey = VALIDATION_SCORE_KEYS[checkType];

      const patchRow = (row: any) =>
        !row || Number(row.id) !== contactId
          ? row
          : {
              ...row,
              validation: {
                ...(row.validation ?? {}),
                [scoreKey]: 100,
                // The server stored the same note. Writing it here too is what
                // makes the cell say "manually updated" straight away rather
                // than looking like a check that scored a perfect 100.
                [MANUAL_SCORES_ROW_KEY]: withManualScoreMark(
                  row.validation?.[MANUAL_SCORES_ROW_KEY] ??
                    row[MANUAL_SCORES_ROW_KEY],
                  checkType
                ),
              },
            };

      setViewContacts((prev) => prev.map(patchRow));
      setBaseViewContacts((prev) => prev.map(patchRow));
    },
    []
  );
  /** Drops a deleted contact from the rows on screen, without a refetch. */
  const applyContactDeleted = useCallback(
    (contactId: number) => {
      const without = (rows: any[]) =>
        rows.filter((row) => Number(row?.id) !== contactId);

      setViewContacts((prev) => without(prev));
      setBaseViewContacts((prev) => without(prev));

      // The pager prefers this count over the row array's length, so leaving it
      // alone would show the old total above one fewer row.
      const viewId = selectedView?.id;

      if (viewId !== undefined) {
        setViewContactCounts((prev) =>
          prev[viewId] === undefined
            ? prev
            : { ...prev, [viewId]: Math.max(0, prev[viewId] - 1) });
      }
    },
    [selectedView?.id]
  );

  const validationFormatters = useMemo(
    () =>
      createValidationFormatters({
        clientId,
        onSuggestionResolved: applySuggestionOutcome,
        onScoreVerified: applyScoreVerified,
        onContactDeleted: applyContactDeleted,
      }),
    [clientId, applySuggestionOutcome, applyScoreVerified, applyContactDeleted]
  );
  const [viewSearchQuery, setViewSearchQuery] = useState("");
  const [viewCurrentPage, setViewCurrentPage] = useState(1);
  const [contactSort, setContactSort] = useState<{ key: string | null; direction: "asc" | "desc" }>({ key: null, direction: "asc" });
  const [viewPageSize, setViewPageSize] = useState<number | "All">(30);
  const [isLoadingViewContacts, setIsLoadingViewContacts] = useState(false);
  const [viewMetaMissing, setViewMetaMissing] = useState(false);
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const [isUpdatingView, setIsUpdatingView] = useState(false);
  const [editorAutoExpand, setEditorAutoExpand] = useState(false);
  const [viewActionsAnchor, setViewActionsAnchor] = useState<string | null>(null);
  const [downloadingViewId, setDownloadingViewId] = useState<number | null>(null);
  const [showBulkUpdatePanel, setShowBulkUpdatePanel] = useState(false);
  const [showSaveSegmentModal, setShowSaveSegmentModal] = useState(false);
  const [isDeletingContact, setIsDeletingContact] = useState(false);
  const [isCloningContact, setIsCloningContact] = useState(false);
  const [isUnsubscribing, setIsUnsubscribing] = useState(false);
  const selectedViewIdRef = useRef<number | null>(null);
  const viewContactsRequestRef = useRef(0);
  const activeViewFetchKeyRef = useRef<string | null>(null);
  const skipNextViewStateSaveRef = useRef(false);

  const activePanel = useSelector(
    (state: RootState) => state.panel.activePanel
  );

  /**
   * A key of its own, for the same reason as the run panel above: views render
   * inside ContactList, which keeps its own bulk update panel bound to its own
   * selection, and the mail dashboard keeps a third. Sharing one key opened
   * them all and left the user updating whichever stacked on top.
   */
  const showBulkUpdatePanelModal =
  activePanel === "bulk-update-view-panel";

  const showSaveSegmentCommonModal =
  activePanel === "save-segment-modal";

  /**
   * A key of its own rather than the list's "validate-contacts-panel": views
   * render inside ContactList, which keeps its own run panel bound to its own
   * selection. Sharing the key would open both at once, the list's one holding
   * no contacts.
   */
  const showValidateViewContactsPanel =
  activePanel === "validate-view-contacts-panel";

  const appModal = useAppModal(); // ✅ correct place
  const { toast, showToast, hideToast } = useToast();
  const { triggerRefresh } = useAppData();
  const showContactMessage = (
    message: string,
    type: "success" | "error" | "info" | "warning"
  ) => {
    showToast(message, type, 3000);
  };

  const {
    layout: persistedColumnLayout,
    saveLayout: onColumnsChange,
    resetLayout: onResetColumns,
  } = useColumnPreferences(clientId, {
    scope: selectedView ? { scopeType: "view", scopeId: selectedView.id } : null,
    customFieldIdByName,
    onError: (message) => showContactMessage(message, "error"),
  });

  const [validationColumnToReveal, setValidationColumnToReveal] = useState<{
    key: string; requestId: number; scopeId: number;
  } | null>(null);
  /**
   * The validation run in flight. Held here rather than in the panel so the
   * toolbar can keep showing its progress after the panel is closed.
   */
  const [activeValidationJob, setActiveValidationJob] = useState<ValidationJob | null>(null);
  const validationProgress = useMemo(
    () =>
      activeValidationJob
        ? {
            processed: activeValidationJob.processedCount,
            total: activeValidationJob.contactCount,
            isFinished: activeValidationJob.isFinished,
            failed: activeValidationJob.status === "failed",
          }
        : null,
    [activeValidationJob]
  );

  useEffect(() => {
    selectedViewIdRef.current = selectedView?.id ?? null;
  }, [selectedView?.id]);
  //------------------------------
  const [selectedContacts, setSelectedContacts] = useState<Set<string>>(new Set());

  /**
   * The selection as the validation API wants it. The grid tracks ids as
   * strings because that is what a row key is; a run is addressed by contact
   * id, and anything unparseable is dropped rather than sent as NaN.
   */
  const validationContactIds = useMemo(
    () =>
      Array.from(selectedContacts)
        .map(Number)
        .filter((id) => !isNaN(id) && id > 0),
    [selectedContacts]
  );

  const handleSelectContact = (contactId: string) => {
    setSelectedContacts(prev => {
      const newSet = new Set(prev);
      if (newSet.has(contactId)) newSet.delete(contactId);
      else newSet.add(contactId);
      return newSet;
    });
  };

const handleSelectAll = (contacts: any[]) => {
  const ids = contacts.map(c => c.id.toString());

  setSelectedContacts(prev => {
    const allSelected = ids.every(id => prev.has(id));
    return allSelected ? new Set() : new Set(ids);
  });
};

const handleUnsubscribeContacts = async () => {
  const ids = Array.from(selectedContacts);
  if (ids.length === 0) return;

  try {
    setIsUnsubscribing(true);

    for (const id of ids) {
      const contact = viewContacts.find((item) => item.id.toString() === id);

      if (!contact?.email) continue;

      const response = await fetch(
        `${API_BASE_URL}/api/Crm/UnsubscribeContacts?ClientId=${clientId}&email=${encodeURIComponent(contact.email)}`
      );

      if (!response.ok) {
        throw new Error("Failed to unsubscribe selected contacts");
      }
    }

    setSelectedContacts(new Set());
    if (selectedView) {
      await fetchContactsForView(selectedView);
    }
    appModal.showSuccess("Selected contacts unsubscribed successfully!");
  } catch (error) {
    console.error("Failed to unsubscribe contacts:", error);
    appModal.showError("Failed to unsubscribe selected contacts");
  } finally {
    setIsUnsubscribing(false);
  }
};

const handleCloneContacts = async () => {
  const ids = Array.from(selectedContacts);
  if (ids.length !== 1) return;

  try {
    setIsCloningContact(true);

    const response = await fetch(`${API_BASE_URL}/api/Crm/clone-contact?contactId=${ids[0]}`, {
      method: "POST",
    });

    if (!response.ok) {
      throw new Error("Failed to clone contact");
    }

    setSelectedContacts(new Set());
    if (selectedView) {
      await fetchContactsForView(selectedView);
    }
    appModal.showSuccess("Contact cloned successfully!");
  } catch (error) {
    console.error("Failed to clone contact:", error);
    appModal.showError("Failed to clone contact");
  } finally {
    setIsCloningContact(false);
  }
};

const handleDeleteContacts = () => {
  const ids = Array.from(selectedContacts);
  if (ids.length === 0) return;

  appModal.showConfirm(
    `Are you sure you want to delete this ${ids.length} contact${ids.length > 1 ? "s" : ""}?`,
    async () => {
      try {
        setIsDeletingContact(true);

        for (const id of ids) {
          const response = await fetch(
            `${API_BASE_URL}/api/Crm/delete-Datafile-contact?contactId=${id}`,
            {
              method: "POST",
              headers: {
                accept: "*/*",
              },
            }
          );

          if (!response.ok) {
            throw new Error(`Failed to delete contact ${id}`);
          }
        }

        setSelectedContacts(new Set());
        if (selectedView) {
          await fetchContactsForView(selectedView);
        }
        appModal.showSuccess(`${ids.length} contact(s) deleted successfully!`);
      } catch (error) {
        console.error("Failed to delete contacts:", error);
        appModal.showError("Failed to delete contacts");
      } finally {
        setIsDeletingContact(false);
      }
    }
  );
};

  // Open a contact's profile in a new tab (used by the name link and by the
  // dedicated profile-icon column shown when the "Full name" column is hidden).
  const openContactProfile = (row: any) => {
    const fallbackDataFileId =
      selectedView?.dataFileIds?.length === 1
        ? selectedView?.dataFileIds?.[0]
        : undefined;
    const fallbackSegmentId =
      selectedView?.segmentIds?.length === 1
        ? selectedView?.segmentIds?.[0]
        : undefined;

    const dataFileId = row.dataFileId || row.DataFileId || fallbackDataFileId;
    const segmentId = row.segmentId || row.SegmentId || fallbackSegmentId;

    const params = new URLSearchParams();
    params.set("tab", "DataCampaigns");
    params.set("subtab", "View");
    params.set("clientId", String(clientId));
    if (segmentId) params.set("segmentId", String(segmentId));
    if (dataFileId != null) params.set("dataFileId", String(dataFileId));
    const query = params.toString();

    const contactDetailsUrl = `/#/contact-details/${row.id}${
      query ? `?${query}` : ""
    }`;
    window.open(contactDetailsUrl, "_blank");
  };

  const normalizedFilterFields = useMemo<FilterBuilderFieldOption[]>(
    () =>
      filterFields.map((field) => ({
        ...field,
        type: normalizeFieldType(field.type),
      })),
    [filterFields]
  );

  const viewColumnNameMap = useMemo(
    () => ({
      ...(columnNameMap || {}),
      ...VALIDATION_COLUMN_LABELS,
      hasOpened: "Opened",
      hasClicked: "Clicked",
    }),
    [columnNameMap]
  );

  const menuBtnStyle: React.CSSProperties = {
    width: "100%",
    padding: "8px 18px",
    textAlign: "left",
    background: "none",
    border: "none",
    color: "#222",
    fontSize: "15px",
    cursor: "pointer",
  };

  const actionIconStyle: React.CSSProperties = {
    width: 24,
    height: 24,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0,
  };

  const getCustomFieldColumns = (data: any[]): CsvColumn[] => {
    const fieldMap = new Map<string, string>();

    filterFields.forEach((field) => {
      if (!field?.key?.startsWith("custom_")) return;
      const label = field.label || field.key.replace(/^custom_/, "");
      const normalized = normalizeCustomFieldKey(label);
      if (!fieldMap.has(normalized)) {
        fieldMap.set(normalized, label);
      }
    });

    data.forEach((contact) => {
      let customFieldsValue = contact?.customFields;
      if (typeof customFieldsValue === "string") {
        try {
          customFieldsValue = JSON.parse(customFieldsValue);
        } catch {
          customFieldsValue = undefined;
        }
      }

      if (customFieldsValue && typeof customFieldsValue === "object") {
        Object.keys(customFieldsValue).forEach((key) => {
          const normalized = normalizeCustomFieldKey(key);
          if (!fieldMap.has(normalized)) {
            fieldMap.set(normalized, key);
          }
        });
      }

      Object.keys(contact || {}).forEach((key) => {
        if (!key.startsWith("custom_")) return;
        const rawKey = key.replace(/^custom_/, "");
        const normalized = normalizeCustomFieldKey(rawKey);
        if (!fieldMap.has(normalized)) {
          fieldMap.set(normalized, rawKey);
        }
      });
    });

    return Array.from(fieldMap.entries()).map(([normalized, label]) => ({
      key: `custom_${normalized}`,
      header: label,
      getValue: (contact: any) => getCustomFieldValue(contact, label),
    }));
  };

  const downloadCSV = (data: any[], filename: string) => {
    if (!data || data.length === 0) {
      showContactMessage("No contacts to download.", "error");
      return;
    }

    const baseColumns: CsvColumn[] = [
      // First column on purpose: this is the id the client keys their CRM
      // records on when we hand updated records back.
      { key: "id", header: "Contact ID" },
      { key: "full_name", header: "Full Name" },
      { key: "first_name", header: "First Name" },
      { key: "last_name", header: "Last Name" },
      { key: "email", header: "Email" },
      { key: "job_title", header: "Job title" },
      { key: "company_name", header: "Company name" },
      { key: "companyTelephone", header: "Company telephone" },
      { key: "website", header: "Website" },
      { key: "country_or_address", header: "Country Or Address" },
      { key: "companyIndustry", header: "Company industry" },
      { key: "linkedin_url", header: "LinkedIn URL" },
      { key: "companyEmployeeCount", header: "Company Employee Count" },
      { key: "companyLinkedInURL", header: "Company LinkedIn URL" },
      { key: "hasLinkedInInfo", header: "LinkedIn Information" },
      { key: "hasNotes", header: "Notes" },
      { key: "hasOpened", header: "Opened" },
      { key: "hasClicked", header: "Clicked" },
      { key: "unsubscribe", header: "Unsubscribe" },
      { key: "created_at", header: "Created date" },
      { key: "updated_at", header: "Updated date" },
      { key: "email_sent_at", header: "Email Sent Date" },
      ...[
        { key: "contactFitConfidence", header: "Target Audience Match", dateKey: "contactFitCheckedAt" },
        { key: "dataIntegrityConfidence", header: "Data Integrity Check", dateKey: "dataIntegrityCheckedAt" },
        { key: "liveContactConfidence", header: "Employment Match", dateKey: "liveContactCheckedAt" },
        { key: "emailValidityConfidence", header: "Email Validity", dateKey: "emailCheckedAt" },
      ].map(({ key, header, dateKey }) => ({
        key,
        header,
        getValue: (contact: any) => {
          const validation = { ...contact, ...contact.validation };
          return verifiedScore(
            validation[key],
            validation.isVerified,
            validation.verifiedAt,
            validation[dateKey]
          );
        },
      })),
    ];

    const customColumns = getCustomFieldColumns(data);
    const columnsToExport = [...baseColumns, ...customColumns];
    const headers = columnsToExport.map((col) => col.header);

    const csvRows = [
      headers.join(","),
      ...data.map((contact) => {
        const row = columnsToExport.map((column) => {
          let value = column.getValue ? column.getValue(contact) : contact[column.key];
          if (value === null || value === undefined) value = "";

          if (
            column.key === "hasLinkedInInfo" ||
            column.key === "hasNotes" ||
            column.key === "hasOpened" ||
            column.key === "hasClicked"
          ) {
            value = value === true ? "Yes" : value === false ? "No" : "";
          }

          // A download is read by other software, so the dates in it stay in
          // the sortable ISO form rather than following the screen's format.
          if (
            (column.key === "created_at" ||
              column.key === "updated_at" ||
              column.key === "email_sent_at") &&
            value
          ) {
            value = formatIsoDate(value);
          }

          const stringValue = String(value);
          if (
            stringValue.includes(",") ||
            stringValue.includes('"') ||
            stringValue.includes("\n") ||
            stringValue.includes("\r")
          ) {
            return `"${stringValue.replace(/"/g, '""')}"`;
          }

          return stringValue;
        });

        return row.join(",");
      }),
    ];

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvRows.join("\n")], {
      type: "text/csv;charset=utf-8;",
    });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);

    link.setAttribute("href", url);
    link.setAttribute("download", `${filename}.csv`);
    link.style.visibility = "hidden";

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const normalizeViewFromApi = (view: any, meta?: ViewMeta): ViewItem => {
    const serverFiltersJson = getFirstDefined(
      view.filtersJson,
      view.filters_json,
      view.filterJson,
      view.filter_json,
      view.filters
    );
    const serverDataFileIds = getFirstDefined(
      view.dataFileIds,
      view.data_file_ids,
      view.dataFileId,
      view.data_file_id
    );
    const serverSegmentIds = getFirstDefined(
      view.segmentIds,
      view.segment_ids,
      view.segmentId,
      view.segment_id
    );
    const serverUseAll = getFirstDefined(
      view.useAllDataFiles,
      view.use_all_datafiles,
      meta?.useAllDataFiles
    );
    const serverExcluded = getFirstDefined(
      view.excludedDataFileIds,
      view.excluded_datafile_ids,
      meta?.excludedDataFileIds
    );

    return {
      ...view,
      ...meta,
      filtersJson:
        normalizeFiltersJson(meta?.filtersJson) ||
        normalizeFiltersJson(serverFiltersJson),
      dataFileIds: normalizeIdArray(meta?.dataFileIds || serverDataFileIds),
      segmentIds: normalizeIdArray(meta?.segmentIds || serverSegmentIds),
      useAllDataFiles: !!serverUseAll,
      excludedDataFileIds: normalizeIdArray(serverExcluded),
    };
  };

  const fetchViews = async () => {
    if (!clientId) return;
    setIsLoadingViews(true);
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/views-by-client?clientId=${clientId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch views");
      }
      const data: any[] = await response.json();
      const metaMap = loadViewMetaMap(clientId);
      const merged = data.map((view) =>
        normalizeViewFromApi(view, metaMap[String(view.id)])
      ).sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
      setViews(merged);
    } catch (error) {
      console.error("Error fetching views:", error);
      setViews([]);
    } finally {
      setIsLoadingViews(false);
    }
  };

  const fetchViewForEdit = async (view: ViewItem): Promise<ViewItem> => {
    const metaMap = loadViewMetaMap(clientId);

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/view-by-id?clientId=${clientId}&viewId=${view.id}`
      );

      if (response.ok) {
        const data = await response.json();
        return normalizeViewFromApi(data, metaMap[String(view.id)]);
      }
    } catch (error) {
      console.warn("Could not fetch view details by id:", error);
    }

    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/views-by-client?clientId=${clientId}`
      );

      if (response.ok) {
        const data: any[] = await response.json();
        const matchedView = data.find((entry) => Number(entry.id) === Number(view.id));
        if (matchedView) {
          return normalizeViewFromApi(matchedView, metaMap[String(view.id)]);
        }
      }
    } catch (error) {
      console.warn("Could not refresh view details from list:", error);
    }

    return normalizeViewFromApi(view, metaMap[String(view.id)]);
  };

  useEffect(() => {
    fetchViews();
  }, [clientId]);

  useEffect(() => {
    if (!clientId || !isActive) return;
    fetchViews();
  }, [clientId, isActive, refreshToken]);

  useEffect(() => {
    if (!clientId) return;

    if (viewMode === "detail" && selectedView?.id) {
      if (skipNextViewStateSaveRef.current) {
        skipNextViewStateSaveRef.current = false;
        return;
      }

      saveViewState(clientId, {
        viewId: selectedView.id,
        viewMode: "detail",
      });
    }
  }, [clientId, viewMode, selectedView?.id]);

  const fetchSources = async () => {
    if (!clientId) return;
    setIsLoadingSources(true);
    try {
      const [dataFilesResponse, segmentsResponse] = await Promise.all([
        fetch(
          `${API_BASE_URL}/api/Crm/datafile-byclientid?clientId=${clientId}`
        ),
        fetch(
          `${API_BASE_URL}/api/Crm/get-segments-by-client?clientId=${clientId}`
        ),
      ]);

      if (dataFilesResponse.ok) {
        const dataFiles = await dataFilesResponse.json();
        const normalized = (dataFiles || [])
          .filter((file: any) => file?.id != null)
          .map((file: any) => ({
            id: Number(file.id),
            name: file.name || file.data_file_name || `List ${file.id}`,
          }));
        setAvailableDataFiles(normalized);
      }

      if (segmentsResponse.ok) {
        const segments = await segmentsResponse.json();
        const normalized = (segments || [])
          .filter((segment: any) => segment?.id != null)
          .map((segment: any) => ({
            id: Number(segment.id),
            name: segment.name || `Segment ${segment.id}`,
          }));
        setAvailableSegments(normalized);
      }
    } catch (error) {
      console.error("Error fetching view sources:", error);
    } finally {
      setIsLoadingSources(false);
    }
  };

  useEffect(() => {
    fetchSources();
  }, [clientId]);

  useEffect(() => {
    setViewMode("list");
    setSelectedView(null);
    setBaseViewContacts([]);
    setViewContacts([]);
    setViewMetaMissing(false);
    setViewContactCounts({});
    setDownloadingViewId(null);
    const pendingViewState = loadViewState(clientId);
    if (pendingViewState?.source !== "campaign-data-source") {
      clearViewState(clientId);
    }
  }, [clientId]);

  useEffect(() => {
    if (!clientId || isLoadingViews || views.length === 0) {
      return;
    }

    const persistedState = loadViewState(clientId);
    if (persistedState?.viewId) {
      const persistedView = views.find((view) => view.id === persistedState.viewId);

      if (!persistedView) {
        clearViewState(clientId);
        return;
      }

      const shouldApplyPersistedView =
        viewMode !== "detail" ||
        selectedView?.id !== persistedState.viewId ||
        persistedState.source === "campaign-data-source";

      if (shouldApplyPersistedView) {
        if (persistedState.source === "campaign-data-source") {
          skipNextViewStateSaveRef.current = true;
          clearViewState(clientId);
        }

        setBaseViewContacts([]);
        setViewContacts([]);
        setSelectedContacts(new Set());
        setViewSearchQuery("");
        setViewCurrentPage(1);
        setViewMetaMissing(false);
        setSelectedView(persistedView);
        setViewMode("detail");
        onViewModeChange?.("detail");
        return;
      }
    }

    if (viewMode === "detail" && selectedView?.id) {
      const latestSelectedView = views.find((view) => view.id === selectedView.id);

      if (!latestSelectedView) {
        setViewMode("list");
        setSelectedView(null);
        setViewContacts([]);
        setViewMetaMissing(false);
        clearViewState(clientId);
        return;
      }

      if (latestSelectedView !== selectedView) {
        setSelectedView(latestSelectedView);
      }

      return;
    }
  }, [clientId, isLoadingViews, onViewModeChange, refreshToken, selectedView, viewMode, views]);

  const handleViewSort = (key: string) => {
    if (viewSortKey === key) {
      setViewSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setViewSortKey(key);
      setViewSortDirection("asc");
    }
    setCurrentPageViews(1);
  };

  const renderViewSortArrow = (columnKey: string) => {
    if (columnKey === viewSortKey) {
      return viewSortDirection === "asc" ? " ▲" : " ▼";
    }
    return "";
  };

  const filteredViews = useMemo(() => {
    const searchLower = viewSearch.toLowerCase();
    let filtered = views.filter((view) => {
      return (
        view.name?.toLowerCase().includes(searchLower) ||
        view.description?.toLowerCase().includes(searchLower) ||
        String(view.id).includes(searchLower)
      );
    });
    if (viewSortKey) {
      filtered = [...filtered].sort((a: any, b: any) => {
        const aVal = (a as any)[viewSortKey] ?? "";
        const bVal = (b as any)[viewSortKey] ?? "";

        const dateA = new Date(aVal);
        const dateB = new Date(bVal);
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return viewSortDirection === "asc"
            ? dateA.getTime() - dateB.getTime()
            : dateB.getTime() - dateA.getTime();
        }

        const numA = Number(aVal);
        const numB = Number(bVal);
        if (!isNaN(numA) && !isNaN(numB)) {
          return viewSortDirection === "asc" ? numA - numB : numB - numA;
        }

        return viewSortDirection === "asc"
          ? String(aVal).toLowerCase().localeCompare(String(bVal).toLowerCase())
          : String(bVal).toLowerCase().localeCompare(String(aVal).toLowerCase());
      });
    } else {
      filtered = [...filtered].sort((a, b) =>
        (a.name || "").localeCompare(b.name || "", undefined, {
          sensitivity: "base",
        })
      );
    }
    return filtered;
  }, [views, viewSearch, viewSortKey, viewSortDirection]);

  useEffect(() => {
    setCurrentPageViews(1);
  }, [viewSearch]);

  const getNumericPageSize = (size: number | "All", totalItems: number) => {
    return size === "All" ? totalItems : size;
  };

  const paginatedViews = useMemo(() => {
    const numericPageSize =
      getNumericPageSize(pageSizeViews, filteredViews.length) || 1;
    const startIndex = (currentPageViews - 1) * numericPageSize;
    const endIndex = startIndex + numericPageSize;
    return filteredViews.slice(startIndex, endIndex);
  }, [filteredViews, currentPageViews, pageSizeViews]);

  const totalPagesViews = useMemo(() => {
    const numericPageSize =
      getNumericPageSize(pageSizeViews, filteredViews.length) || 1;
    return Math.ceil(filteredViews.length / numericPageSize);
  }, [filteredViews.length, pageSizeViews]);

  const evaluateCondition = (
    row: Record<string, any>,
    condition: FilterCondition,
    campaignIndexes: Awaited<ReturnType<typeof buildTrackingIndexesForGroups>>
  ) => {
    if (conditionRequiresCampaign(condition)) {
      return evaluateTrackingCondition(row, condition, campaignIndexes);
    }

    const value = getRowValue(row, condition.field);

    // Dates are compared by calendar day, in the account's time zone, so that
    // Today / Yesterday / Tomorrow mean the day the view is opened on.
    const fieldType = normalizedFilterFields.find(
      (field) => field.key === condition.field
    )?.type;
    if (fieldType === "date" || isRelativeDateToken(condition.value)) {
      const dayResult = matchesDayCondition(
        value,
        condition.operator,
        condition.value
      );
      if (dayResult !== null) {
        return dayResult;
      }
    }

    switch (condition.operator) {
      case "contains":
        if (Array.isArray(condition.value)) {
          if (condition.value.length === 0) return true;
          // Multiple selected options are OR-ed together.
          return condition.value.some((entry) =>
            String(value).toLowerCase().includes(String(entry).toLowerCase())
          );
        }
        return String(value)
          .toLowerCase()
          .includes(String(condition.value).toLowerCase());
      case "equals": {
        if (Array.isArray(condition.value)) {
          if (condition.value.length === 0) return true;
          // Multiple selected options are OR-ed together (match any).
          return condition.value.some(
            (entry) => String(value).toLowerCase() === String(entry).toLowerCase()
          );
        }
        return String(value).toLowerCase() === String(condition.value).toLowerCase();
      }
      case "notEquals": {
        if (Array.isArray(condition.value)) {
          if (condition.value.length === 0) return true;
          // Must differ from every selected option.
          return condition.value.every(
            (entry) => String(value).toLowerCase() !== String(entry).toLowerCase()
          );
        }
        return String(value).toLowerCase() !== String(condition.value).toLowerCase();
      }
      case "startsWith":
        return String(value)
          .toLowerCase()
          .startsWith(String(condition.value).toLowerCase());
      case "endsWith":
        return String(value)
          .toLowerCase()
          .endsWith(String(condition.value).toLowerCase());
      case "gt":
        return Number(value) > Number(condition.value);
      case "lt":
        return Number(value) < Number(condition.value);
      case "gte":
        return Number(value) >= Number(condition.value);
      case "lte":
        return Number(value) <= Number(condition.value);
      case "before":
        return new Date(value) < new Date(condition.value);
      case "after":
        return new Date(value) > new Date(condition.value);
      case "isEmpty":
        return (
          value === null ||
          value === undefined ||
          String(value).trim() === ""
        );
      case "isNotEmpty":
        return !(
          value === null ||
          value === undefined ||
          String(value).trim() === ""
        );
      default:
        return true;
    }
  };

  const applySavedFilters = async (rows: any[], filtersJson?: string) => {
    const parsed = parseFiltersJson(filtersJson);
    const groups: FilterGroup[] = (parsed.groups || [])
      .map((group, groupIndex) => ({
        id: group.id || `group-${groupIndex}`,
        joinWithPrevious:
          groupIndex === 0 ? undefined : group.joinWithPrevious || "AND",
        conditions: (group.conditions || []).filter((condition) =>
          isCompleteCondition(condition)
        ),
      }))
      .filter((group) => group.conditions.length > 0);

    if (groups.length === 0) {
      return rows;
    }

    const campaignIndexes = await buildTrackingIndexesForGroups(clientId, groups);

    return rows.filter((row) =>
      groups.reduce((groupResult, group, groupIndex) => {
        const conditionsResult = group.conditions.reduce(
          (result, condition, index) => {
            const evaluation = evaluateCondition(row, condition, campaignIndexes);
            if (index === 0) return evaluation;
            return condition.joinWithPrevious === "OR"
              ? result || evaluation
              : result && evaluation;
          },
          true as boolean
        );

        if (groupIndex === 0) {
          return conditionsResult;
        }

        return group.joinWithPrevious === "OR"
          ? groupResult || conditionsResult
          : groupResult && conditionsResult;
      }, true as boolean)
    );
  };

  const decorateContactsWithTrackingState = async (
    rows: any[],
    filtersJson?: string
  ) => {
    const parsed = parseFiltersJson(filtersJson);
    const groups: FilterGroup[] = (parsed.groups || [])
      .map((group, groupIndex) => ({
        id: group.id || `group-${groupIndex}`,
        joinWithPrevious:
          groupIndex === 0 ? undefined : group.joinWithPrevious || "AND",
        conditions: (group.conditions || []).filter((condition) =>
          isCompleteCondition(condition)
        ),
      }))
      .filter((group) => group.conditions.length > 0);

    const trackingCampaignIds = getTrackingCampaignIds(groups);

    if (trackingCampaignIds.length === 0) {
      return rows.map((row) => ({
        ...row,
        hasOpened: false,
        hasClicked: false,
      }));
    }

    const campaignIndexes = await buildTrackingIndexesForGroups(clientId, groups);

    return rows.map((row) => ({
      ...row,
      hasOpened: trackingCampaignIds.some((campaignId) =>
        evaluateTrackingCondition(
          row,
          {
            id: `tracking-open-${campaignId}`,
            field: TRACKING_OPEN_FIELD,
            operator: "equals",
            value: "true",
            context: { campaignId },
          },
          campaignIndexes
        )
      ),
      hasClicked: trackingCampaignIds.some((campaignId) =>
        evaluateTrackingCondition(
          row,
          {
            id: `tracking-click-${campaignId}`,
            field: TRACKING_CLICK_FIELD,
            operator: "equals",
            value: "true",
            context: { campaignId },
          },
          campaignIndexes
        )
      ),
    }));
  };

  // Fetch the set of unsubscribed emails for this client so the client-side
  // view path can exclude them exactly like the backend `view-contacts`
  // endpoint does (the backend filters against the UnsubscribedContacts table
  // by email). Best-effort: if the endpoint is unavailable we return an empty
  // set and fall back to the previous behaviour rather than breaking the view.
  const fetchUnsubscribedEmails = async (): Promise<Set<string>> => {
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/unsubscribed-emails-by-client?clientId=${clientId}`
      );
      if (!response.ok) {
        return new Set();
      }
      const data = await response.json();
      const emails: any[] = Array.isArray(data) ? data : data?.emails ?? [];
      return new Set(
        emails
          .map((email) => String(email ?? "").trim().toLowerCase())
          .filter((email) => email.length > 0)
      );
    } catch (error) {
      console.warn("Failed to load unsubscribed emails:", error);
      return new Set();
    }
  };

  const fetchViewContactsData = async (
    view: ViewItem,
    page: number = viewCurrentPage,
    pageSize: number | "All" = viewPageSize,
    search: string = viewSearchQuery,
    includeEmailContent: boolean = false,
    filtersJsonOverride: string | null = null
  ): Promise<{ contacts: any[]; metaMissing: boolean; contactCount: number }> => {
    const allDataFileIds = availableDataFiles.map((file) => file.id);
    const excludedDataFileIds = view.excludedDataFileIds || [];
    const effectiveAllDataFileIds = allDataFileIds.filter(
      (id) => !excludedDataFileIds.includes(id)
    );
    const dataFileIds =
      view.useAllDataFiles && effectiveAllDataFileIds.length > 0
        ? effectiveAllDataFileIds
        : view.dataFileIds || [];
    const segmentIds = view.segmentIds || [];
    // An applied-but-unsaved filter stands in for the view's saved one.
    const effectiveFiltersJson = filtersJsonOverride ?? view.filtersJson;
    const hasLocalFilters = !!effectiveFiltersJson;
    const hasLocalSources = dataFileIds.length > 0 || segmentIds.length > 0;

    // View contacts are always paged by the backend. The local fallback below
    // remains only for invalid/legacy view ids.
    if (Number(view.id) > 0) {
      try {
        const response = await fetch(`${API_BASE_URL}/api/Crm/view-contacts`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            clientId: Number(clientId),
            viewId: Number(view.id),
            page,
            pageSize: pageSize === "All" ? 0 : pageSize,
            sortBy: contactSort.key,
            sortDirection: contactSort.direction,
            search: search.trim(),
            includeEmailContent,
            // Omitted unless the editor has an unsaved filter applied, so the
            // server keeps using the view's own filters_json.
            ...(filtersJsonOverride == null
              ? {}
              : { filtersJson: filtersJsonOverride }),
          }),
        });

        if (!response.ok) {
          throw new Error("Failed to fetch view contacts from server");
        }

        const data = await response.json();
        const contacts = await decorateContactsWithTrackingState(
          data.contacts || [],
          effectiveFiltersJson
        );
        return {
          contacts,
          metaMissing: false,
          contactCount: data.contactCount ?? data.total ?? contacts.length,
        };
      } catch (error) {
        console.warn("Server fallback for view contacts failed:", error);
        return { contacts: [], metaMissing: true, contactCount: 0 };
      }
    }

    if (dataFileIds.length === 0 && segmentIds.length === 0) {
      return { contacts: [], metaMissing: true, contactCount: 0 };
    }

    let segmentDataFileMap: Record<number, number> = {};
    if (segmentIds.length > 0) {
      try {
        const segmentResponse = await fetch(
          `${API_BASE_URL}/api/Crm/get-segments-by-client?clientId=${clientId}`
        );
        if (segmentResponse.ok) {
          const segmentData = await segmentResponse.json();
          segmentDataFileMap = (segmentData || []).reduce(
            (acc: Record<number, number>, segment: any) => {
              if (segment?.id != null && segment?.dataFileId != null) {
                acc[segment.id] = segment.dataFileId;
              }
              return acc;
            },
            {}
          );
        }
      } catch (error) {
        console.warn("Failed to load segment metadata:", error);
      }
    }

    const dataFileRequests = dataFileIds.map(async (dataFileId) => {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/contacts/List-by-ClientId?clientId=${clientId}&dataFileId=${dataFileId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch contacts for view");
      }
      const data = await response.json();
      const contacts = data.contacts || [];
      return contacts.map((contact: any) => ({
        ...contact,
        dataFileId,
      }));
    });

    const segmentRequests = segmentIds.map(async (segmentId) => {
      const response = await fetch(
        `${API_BASE_URL}/api/Crm/segment-contacts?clientId=${clientId}&segmentId=${segmentId}`
      );
      if (!response.ok) {
        throw new Error("Failed to fetch segment contacts for view");
      }
      const data = await response.json();
      const contacts = data.contacts || [];
      const fallbackDataFileId = segmentDataFileMap[segmentId];
      return contacts.map((contact: any) => ({
        ...contact,
        segmentId,
        dataFileId: contact.dataFileId ?? fallbackDataFileId,
      }));
    });

    const results = await Promise.all([
      Promise.allSettled(dataFileRequests),
      Promise.allSettled(segmentRequests),
    ]);

    const mergedContacts: any[] = [];
    results.forEach((resultGroup) => {
      resultGroup.forEach((result) => {
        if (result.status === "fulfilled") {
          mergedContacts.push(...result.value);
        }
      });
    });

    const mergedMap = new Map<number, any>();
    mergedContacts.forEach((contact) => {
      const existing = mergedMap.get(contact.id);
      if (existing) {
        mergedMap.set(contact.id, { ...existing, ...contact });
      } else {
        mergedMap.set(contact.id, contact);
      }
    });

    const mergedList = Array.from(mergedMap.values());

    // Exclude unsubscribed contacts to match the backend view-contacts logic.
    const unsubscribedEmails = await fetchUnsubscribedEmails();
    const subscribedList =
      unsubscribedEmails.size === 0
        ? mergedList
        : mergedList.filter((contact) => {
            const email = String(contact.email ?? "").trim().toLowerCase();
            return email.length === 0 || !unsubscribedEmails.has(email);
          });

    const filtered = await applySavedFilters(subscribedList, effectiveFiltersJson);
    const decorated = await decorateContactsWithTrackingState(
      filtered,
      effectiveFiltersJson
    );

    return {
      contacts: decorated,
      metaMissing: false,
      contactCount: decorated.length,
    };
  };

  const updateViewCountCache = (viewId: number, count: number) => {
    setViewContactCounts((prev) => ({ ...prev, [viewId]: count }));
    setViews((prev) =>
      prev.map((item) =>
        item.id === viewId ? { ...item, contactCount: count } : item
      )
    );
  };

  const fetchContactsForView = async (
    view: ViewItem,
    // Passed explicitly rather than read from state, so a caller that has just
    // cleared the draft is not served from a stale closure.
    filtersJsonOverride: string | null = draftFiltersJson
  ) => {
    const fetchKey = `${view.id}|${viewCurrentPage}|${viewPageSize}|${viewSearchQuery.trim()}|${contactSort.key}|${contactSort.direction}|${filtersJsonOverride ?? ""}`;
    if (activeViewFetchKeyRef.current === fetchKey) {
      return;
    }
    activeViewFetchKeyRef.current = fetchKey;

    const requestId = viewContactsRequestRef.current + 1;
    viewContactsRequestRef.current = requestId;

    const isLatestRequest = () =>
      viewContactsRequestRef.current === requestId &&
      selectedViewIdRef.current === view.id;

    // Force clear all previous data FIRST
    setBaseViewContacts([]);
    setViewContacts([]);
    setIsLoadingViewContacts(true);
    setViewMetaMissing(false);
    
    try {
      const { contacts, metaMissing, contactCount } = await fetchViewContactsData(
        view,
        viewCurrentPage,
        viewPageSize,
        viewSearchQuery,
        false,
        filtersJsonOverride
      );

      if (!isLatestRequest()) {
        return;
      }
      
      // Only update if this is still the selected view (avoid race conditions)
      setBaseViewContacts(contacts);
      setViewContacts(contacts);
      setViewMetaMissing(metaMissing);
      if (!metaMissing) {
        // An unsaved filter's count belongs to the pager only - writing it to
        // the cache would relabel the view in the list with a count its saved
        // filters do not produce.
        if (filtersJsonOverride == null) {
          setDraftFilteredTotal(null);
          updateViewCountCache(view.id, contactCount);
        } else {
          setDraftFilteredTotal(contactCount);
        }
      }
    } catch (error) {
      if (!isLatestRequest()) {
        return;
      }

      console.error("Error fetching view contacts:", error);
      setBaseViewContacts([]);
      setViewContacts([]);
      setViewMetaMissing(false);
      setDraftFilteredTotal(null);
      showContactMessage("Failed to load view contacts.", "error");
    } finally {
      if (activeViewFetchKeyRef.current === fetchKey) {
        activeViewFetchKeyRef.current = null;
      }
      if (isLatestRequest()) {
        setIsLoadingViewContacts(false);
      }
    }
  };

  const handleDownloadView = async (view: ViewItem) => {
    setDownloadingViewId(view.id);
    try {
      const { contacts, metaMissing, contactCount } = await fetchViewContactsData(
        view,
        1,
        "All",
        "",
        true
      );

      if (metaMissing) {
        showContactMessage(
          "This view is missing saved filter metadata in this browser, so it cannot be downloaded yet.",
          "error"
        );
        return;
      }

      if (!contacts.length) {
        showContactMessage("No contacts to download.", "error");
        updateViewCountCache(view.id, 0);
        return;
      }

      updateViewCountCache(view.id, contactCount);
      const filename = `${view.name.replace(/[^a-z0-9]/gi, "_")}_${
        new Date().toISOString().split("T")[0]
      }`;
      downloadCSV(contacts, filename);
    } catch (error) {
      console.error("Error downloading view:", error);
      showContactMessage("Failed to download view data.", "error");
    } finally {
      setDownloadingViewId(null);
      setViewActionsAnchor(null);
    }
  };

  const selectedViewDataFileKey = selectedView?.dataFileIds?.join(",") || "";
  const selectedViewSegmentKey = selectedView?.segmentIds?.join(",") || "";
  const selectedViewExcludedDataFileKey =
    selectedView?.excludedDataFileIds?.join(",") || "";

  useEffect(() => {
    if (viewMode === "detail" && selectedView) {
      // Immediately clear to show loading state
      setBaseViewContacts([]);
      setViewContacts([]);
      setIsLoadingViewContacts(true);
      
      // Small delay to ensure state is cleared before fetching
      const timer = setTimeout(() => {
        fetchContactsForView(selectedView);
      }, viewSearchQuery ? 300 : 0);
      
      return () => clearTimeout(timer);
    }
  }, [
    viewMode,
    selectedView?.id,
    selectedView?.filtersJson,
    selectedView?.useAllDataFiles,
    selectedViewDataFileKey,
    selectedViewSegmentKey,
    selectedViewExcludedDataFileKey,
    availableDataFiles.length,
    viewCurrentPage,
    viewPageSize,
    contactSort,
    viewSearchQuery,
    draftFiltersJson,
  ]);

  const handleDeleteView = async (view: ViewItem) => {
    appModal.showConfirm(
      `Are you sure you want to delete view "${view.name}"?`,
      async () => {
        try {
          const response = await fetch(
            `${API_BASE_URL}/api/Crm/delete-view?viewId=${view.id}`,
            { method: "POST" }
          );
          if (!response.ok) {
            throw new Error("Failed to delete view");
          }
          const metaMap = loadViewMetaMap(clientId);
          delete metaMap[String(view.id)];
          saveViewMetaMap(clientId, metaMap);
          setViews((prev) => prev.filter((item) => item.id !== view.id));
          triggerRefresh();
          if (selectedView?.id === view.id) {
            setSelectedView(null);
            setViewMode("list");
            setBaseViewContacts([]);
            setViewContacts([]);
            setViewMetaMissing(false);
            clearViewState(clientId);
          }
          showContactMessage("View deleted successfully.", "success");
        } catch (error) {
          console.error("Error deleting view:", error);
          showContactMessage("Failed to delete view.", "error");
        }
      },
      "Delete view",
      "Delete",
      "Cancel"
    );
  };

  const openView = async (view: ViewItem, expandEditor = false) => {
    setViewActionsAnchor(null);
    setEditorAutoExpand(expandEditor);
    setSelectedView(view);
    setViewMode("detail");
    onViewModeChange?.("detail");
    setSelectedContacts(new Set());
    setViewSearchQuery("");
    setViewCurrentPage(1);
    setDraftFiltersJson(null);
    setDraftFilteredTotal(null);

    // Always refresh the source list so "all lists" views immediately reflect
    // newly added lists.
    fetchSources();

    // Fetch the authoritative view config (useAllDataFiles / excluded / filters).
    // The list endpoint does not reliably return useAllDataFiles, which caused
    // "all lists" views to show only the originally-saved lists until the user
    // opened the edit panel. Refreshing here keeps the detail view correct.
    try {
      const fresh = await fetchViewForEdit(view);
      setViews((prev) =>
        prev.map((item) => (item.id === fresh.id ? fresh : item))
      );
      setSelectedView((prev) => (prev?.id === fresh.id ? fresh : prev));
    } catch (error) {
      console.warn("Could not refresh view on open:", error);
    }
  };

  // Opening "Edit" from the list now opens the view detail with the inline
  // editor expanded (the side panel has been replaced by the inline editor).
  const openEditPanel = (view: ViewItem) => {
    openView(view, true);
  };

  const handleSaveViewChanges = async (payload: ViewEditorPayload) => {
    if (!selectedView) return;
    setIsUpdatingView(true);
    try {
      const useAllDataFiles = payload.useAllDataFiles;
      const dataFileIds = useAllDataFiles ? [] : payload.dataFileIds;
      const excludedDataFileIds = useAllDataFiles
        ? payload.excludedDataFileIds
        : [];
      const response = await fetch(`${API_BASE_URL}/api/Crm/update-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          viewId: selectedView.id,
          name: payload.name,
          description: payload.description,
          filtersJson: payload.filtersJson,
          dataFileIds,
          segmentIds: payload.segmentIds,
          useAllDataFiles,
          excludedDataFileIds,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to update view");
      }

      const updatedView: ViewItem = {
        ...selectedView,
        name: payload.name,
        description: payload.description,
        filtersJson: payload.filtersJson,
        dataFileIds,
        segmentIds: payload.segmentIds,
        useAllDataFiles,
        excludedDataFileIds,
      };

      setViews((prev) =>
        prev.map((view) => (view.id === updatedView.id ? updatedView : view))
      );
      setSelectedView(updatedView);

      const metaMap = loadViewMetaMap(clientId);
      metaMap[String(updatedView.id)] = {
        filtersJson: payload.filtersJson,
        dataFileIds,
        segmentIds: payload.segmentIds,
        useAllDataFiles,
        excludedDataFileIds,
      };
      saveViewMetaMap(clientId, metaMap);

      triggerRefresh();
      setDraftFiltersJson(null);
      setDraftFilteredTotal(null);
      await fetchContactsForView(updatedView, null);
      showContactMessage("View updated successfully.", "success");
    } catch (error) {
      console.error("Error updating view:", error);
      showContactMessage("Failed to update view.", "error");
    } finally {
      setIsUpdatingView(false);
    }
  };

  const handleCreateViewFromEditor = async (payload: ViewEditorPayload) => {
    setIsUpdatingView(true);
    try {
      const useAllDataFiles = payload.useAllDataFiles;
      const dataFileIds = useAllDataFiles ? [] : payload.dataFileIds;
      const excludedDataFileIds = useAllDataFiles
        ? payload.excludedDataFileIds
        : [];
      const response = await fetch(`${API_BASE_URL}/api/Crm/create-view`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          clientId: Number(clientId),
          name: payload.name,
          description: payload.description,
          filtersJson: payload.filtersJson,
          dataFileIds,
          segmentIds: payload.segmentIds,
          useAllDataFiles,
          excludedDataFileIds,
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Failed to create view");
      }

      const savedView = await response.json();
      if (savedView?.id != null) {
        const metaMap = loadViewMetaMap(clientId);
        metaMap[String(savedView.id)] = {
          filtersJson: payload.filtersJson,
          dataFileIds,
          segmentIds: payload.segmentIds,
          useAllDataFiles,
          excludedDataFileIds,
        };
        saveViewMetaMap(clientId, metaMap);
      }

      await fetchViews();
      triggerRefresh();
      showContactMessage(
        `New view "${savedView?.name || payload.name || "Untitled view"}" created successfully.`,
        "success"
      );
    } catch (error) {
      console.error("Error creating view:", error);
      showContactMessage("Failed to create view.", "error");
    } finally {
      setIsUpdatingView(false);
    }
  };

  const detailHeader = viewMetaMissing ? (
    <div
      style={{
        marginBottom: 16,
        padding: "12px 16px",
        background: "#fff7ed",
        borderRadius: 6,
        border: "1px solid #fed7aa",
        color: "#9a3412",
      }}
    >
      This view was saved on the server, but its filter details are
      not cached in this browser yet. Open the list where it was
      created and save it again to make it usable here.
    </div>
  ) : (
  <>
    {selectedView && (
      <div style={{ marginBottom: 20 }}>
        <FilterBuilder
          key={`${selectedView.id}-${selectedView.filtersJson || ""}`}
          data={baseViewContacts}
          fields={normalizedFilterFields}
          clientId={clientId}
          initialFiltersJson={selectedView.filtersJson}
          onFiltered={(filteredData) => {
            setViewContacts(filteredData);
            setSelectedContacts(new Set());
          }}
          // The rows on screen are one page of the view, so the filter goes to
          // the server and the whole view is matched against it - same engine
          // the view uses once the filter is saved.
          onApplyFilters={(appliedFiltersJson) => {
            setDraftFiltersJson(appliedFiltersJson);
            setDraftFilteredTotal(null);
            setViewCurrentPage(1);
            setSelectedContacts(new Set());
          }}
          viewEditor={{
            availableDataFiles,
            availableSegments,
            isLoadingSources,
            startExpanded: editorAutoExpand,
            allowCreateNew: true,
            isSaving: isUpdatingView,
            initialName: selectedView.name,
            initialDescription: selectedView.description,
            initialDataFileIds: selectedView.dataFileIds || [],
            initialSegmentIds: selectedView.segmentIds || [],
            initialUseAllDataFiles: !!selectedView.useAllDataFiles,
            initialExcludedDataFileIds: selectedView.excludedDataFileIds || [],
            onSaveChanges: handleSaveViewChanges,
            onCreateNew: handleCreateViewFromEditor,
          }}
        />
      </div>
    )}

    {/* ✅ BULK ACTION BAR */}
{selectedContacts.size > 0 && (
  <div
    style={{
      marginBottom: 16,
      padding: "12px 16px",
      background: "#f0f7ff",
      borderRadius: 6,
      display: "flex",
      alignItems: "center",
      gap: 16,
    }}
  >
    <span style={{ fontWeight: 500 }}>
      {selectedContacts.size} contact
      {selectedContacts.size > 1 ? "s" : ""} selected
    </span>

    <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
      {selectedContacts.size === 1 && (
        <button
          className="button secondary"
          onClick={handleCloneContacts}
          disabled={isCloningContact}
          style={{
            background: "none",
            color: "#3f9f42",
            border: "none",
            borderRadius: "12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            width: "40px",
            height: "40px",
            padding: "0",
            cursor: isCloningContact ? "not-allowed" : "pointer",
            opacity: isCloningContact ? 0.6 : 1,
          }}
          title={isCloningContact ? "Cloning..." : "Clone contact"}
        >
          <img
            src={duplicateIcon}
            alt="Clone"
            style={{
              width: 22,
              height: 22,
              objectFit: "contain",
              filter:
                "invert(47%) sepia(82%) saturate(397%) hue-rotate(84deg) brightness(95%) contrast(90%)",
            }}
          />
        </button>
      )}

      <button
        className="button secondary"
        onClick={handleDeleteContacts}
        disabled={isDeletingContact}
        style={{
          background: "none",
          color: "#3f9f42",
          border: "none",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          padding: "0",
          cursor: isDeletingContact ? "not-allowed" : "pointer",
          opacity: isDeletingContact ? 0.6 : 1,
        }}
        title={isDeletingContact ? "Deleting..." : "Delete contacts"}
      >
        <FontAwesomeIcon
          icon={faTrashAlt}
          style={{ fontSize: 20, color: "#3f9f42" }}
        />
      </button>

      <button
        className="button secondary"
        onClick={handleUnsubscribeContacts}
        disabled={isUnsubscribing}
        style={{
          background: "none",
          color: "#3f9f42",
          border: "none",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          padding: "0",
          cursor: isUnsubscribing ? "not-allowed" : "pointer",
          opacity: isUnsubscribing ? 0.6 : 1,
        }}
        title={isUnsubscribing ? "Processing..." : "Unsubscribe"}
      >
        <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" height="22" width="22">
          <path stroke="#3f9f42" strokeLinecap="round" strokeLinejoin="round" d="M11.25 17.25c0 1.5913 0.6321 3.1174 1.7574 4.2426 1.1252 1.1253 2.6513 1.7574 4.2426 1.7574 1.5913 0 3.1174 -0.6321 4.2426 -1.7574 1.1253 -1.1252 1.7574 -2.6513 1.7574 -4.2426 0 -1.5913 -0.6321 -3.1174 -1.7574 -4.2426 -1.1252 -1.1253 -2.6513 -1.7574 -4.2426 -1.7574 -1.5913 0 -3.1174 0.6321 -4.2426 1.7574 -1.1253 1.1252 -1.7574 2.6513 -1.7574 4.2426Z" strokeWidth="2"></path>
          <path stroke="#3f9f42" strokeLinecap="round" strokeLinejoin="round" d="M14.25 17.25h6" strokeWidth="2"></path>
          <path stroke="#3f9f42" strokeLinecap="round" strokeLinejoin="round" d="M8.25 15.75h-6c-0.39782 0 -0.77936 -0.158 -1.06066 -0.4393C0.908035 15.0294 0.75 14.6478 0.75 14.25v-12c0 -0.39782 0.158035 -0.77936 0.43934 -1.06066C1.47064 0.908035 1.85218 0.75 2.25 0.75h18c0.3978 0 0.7794 0.158035 1.0607 0.43934 0.2813 0.2813 0.4393 0.66284 0.4393 1.06066V9" strokeWidth="2"></path>
          <path stroke="#3f9f42" strokeLinecap="round" strokeLinejoin="round" d="m21.41 1.30005 -8.143 6.264c-0.5783 0.44486 -1.2874 0.68606 -2.017 0.68606 -0.7296 0 -1.43873 -0.2412 -2.01701 -0.68606l-8.144 -6.264" strokeWidth="2"></path>
        </svg>
      </button>

      <button
        className="button primary"
        onClick={() => 
          //setShowSaveSegmentModal(true)
          dispatch(openPanel("save-segment-modal"))

        }
        style={{
          backgroundColor: "transparent",
          borderColor: "transparent",
          color: "#3f9f42",
          border: "none",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          padding: "0",
          cursor: "pointer",
        }}
        title="Segment"
      >
        <svg
          width="30"
          height="30"
          viewBox="0 0 100 100"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M50 50H85C85 69.33 69.33 85 50 85C30.67 85 15 69.33 15 50C15 30.67 30.67 15 50 15V50Z"
            stroke="#3f9f42"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M60 40V15C73.8071 15 85 26.1929 85 40H60Z"
            stroke="#3f9f42"
            strokeWidth="6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <button
        className="button secondary"
        onClick={() => 
          //setShowBulkUpdatePanel(true)
          dispatch(openPanel("bulk-update-view-panel"))
        }
        style={{
          background: "none",
          color: "#3f9f42",
          border: "none",
          borderRadius: "12px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "40px",
          height: "40px",
          padding: "0",
          cursor: "pointer",
        }}
        title="Bulk Update"
      >
        <FontAwesomeIcon
          icon={faEdit}
          style={{ fontSize: 20, color: "#3f9f42" }}
        />
      </button>

    </div>
  </div>
)}

  </>
);

  return (
    <div className="list-content">
      <div className="section-wrapper">
        {viewMode === "list" ? (
          <>
            <ContactsToolbar
              search={viewSearch}
              onSearchChange={setViewSearch}
              sortKey={viewSortKey}
              sortDirection={viewSortDirection}
              onSort={() => handleViewSort("created_at")}
              currentPage={currentPageViews}
              totalPages={totalPagesViews}
              pageSize={pageSizeViews}
              totalRecords={filteredViews.length}
              onPageChange={setCurrentPageViews}
              onPageSizeChange={setPageSizeViews}
              placeholder="Search a view name or ID"
            />
            <ContactsListsRows
              data={paginatedViews.map((v) => ({
                id: v.id,
                name: v.name,
                description: v.description,
                created_at: v.created_at,
                contactCount: viewContactCounts[v.id] ?? v.contactCount,
              })) as any[]}
              isLoading={isLoadingViews}
              sortKey={viewSortKey}
              sortDirection={viewSortDirection}
              onSort={handleViewSort}
              actionsAnchor={viewActionsAnchor}
              setActionsAnchor={setViewActionsAnchor}
              onRowClick={(file) => { 
                const v = paginatedViews.find((x) => x.id === file.id); 
                if (v) {
                  // Clear previous view data immediately
                  setBaseViewContacts([]);
                  setViewContacts([]);
                  setIsLoadingViewContacts(true);
                  openView(v);
                }
              }}
              onRename={(file) => { const v = paginatedViews.find((x) => x.id === file.id); if (v) openEditPanel(v); }}
              onView={(file) => { 
                const v = paginatedViews.find((x) => x.id === file.id); 
                if (v) {
                  // Clear previous view data immediately
                  setBaseViewContacts([]);
                  setViewContacts([]);
                  setIsLoadingViewContacts(true);
                  openView(v);
                }
              }}
              onDownload={(file) => { const v = paginatedViews.find((x) => x.id === file.id); if (v) handleDownloadView(v); }}
              onDelete={(file) => { const v = paginatedViews.find((x) => x.id === file.id); if (v) handleDeleteView(v); }}
              isDemoAccount={false}
              formatDate={formatDate}
            />
          </>
        ) : (
          <div style={{ padding: "20px 32px 24px" }}>
            <DynamicContactsTable
              key={`view-${clientId}-${selectedView?.id}`}
              revealColumn={validationColumnToReveal?.scopeId === selectedView?.id ? validationColumnToReveal : null}
              onRevealHandled={() => setValidationColumnToReveal(null)}
              showDetailToggle
              validationAction={{
                onClick: () => dispatch(openPanel("validate-view-contacts-panel")),
                selectedCount: selectedContacts.size,
                progress: validationProgress,
              }}
              customAttributeClientId={clientId}
              data={viewContacts}
              isLoading={isLoadingViewContacts}
              search={viewSearchQuery}
              setSearch={(value) => {
                setViewSearchQuery(value);
                setViewCurrentPage(1);
              }}
              showCheckboxes={true}
              paginated={true}
              serverSidePagination={true}
              serverSort={contactSort}
              onSortChange={(sort) => { setContactSort(sort); setViewCurrentPage(1); }}
              currentPage={viewCurrentPage}
              pageSize={viewPageSize}
              onPageChange={setViewCurrentPage}
              onPageSizeChange={(size) => {
                setViewPageSize(size);
                setViewCurrentPage(1);
              }}
              onOpenProfile={openContactProfile}
              leadingColumn={{
                header: "Image",
                width: "64px",
                render: (row: any) => (
                  <ViewContactProfileAvatar
                    key={`${row.id}-${clientId}`}
                    contact={row}
                    clientId={clientId}
                    onOpen={openContactProfile}
                  />
                ),
              }}
              totalItems={
                draftFiltersJson != null
                  ? draftFilteredTotal ?? viewContacts.length
                  : selectedView
                  ? viewContactCounts[selectedView.id] ?? viewContacts.length
                  : viewContacts.length
              }
              autoGenerateColumns={true}
              selectedItems={selectedContacts}
              onSelectItem={handleSelectContact}
              excludeFields={[
                "email_body",
                "email_subject",
                "dataFileId",
                "data_file",
                "customFields",
                ...VALIDATION_EXCLUDED_FIELDS,
              ]}
              onColumnsChange={onColumnsChange}
              onResetColumns={onResetColumns}
              currentTab={currentTab}
              persistedColumnLayout={persistedColumnLayout}
              defaultVisibleColumns={defaultVisibleColumns}
              customFormatters={{
                ...validationFormatters,
                /**
                 * The contact's own id rather than the per-page row counter, so
                 * the column means the same thing here as in a download and in
                 * the client's CRM.
                 */
                id: (value: any) =>
                  value === null || value === undefined ? "-" : String(value),
                first_name: (value: any, row: any) => {
                  const { firstName, fullName } = getContactNameParts(row);
                  return firstName || fullName || "-";
                },
                last_name: (value: any, row: any) => {
                  const { lastName, fullName } = getContactNameParts(row);
                  return lastName || fullName || "-";
                },
                full_name: (value: any, row: any) => {
                  const displayName = getDisplayName(row);
                  if (!displayName || displayName === "-") return "-";

                  return (
                    <span
                      style={{
                        color: "#3f9f42",
                        textDecoration: "underline",
                        cursor: "pointer",
                        fontWeight: 500,
                      }}
                      onClick={(event) => {
                        event.stopPropagation();
                        openContactProfile(row);
                      }}
                    >
                      {displayName}
                    </span>
                  );
                },
                created_at: (value: any) => formatDate(value),
                updated_at: (value: any) => formatDate(value),
                email_sent_at: (value: any) => formatDate(value),
                hasOpened: (value: any) => (value ? "Yes" : "No"),
                hasClicked: (value: any) => (value ? "Yes" : "No"),
                website: (value: any) => {
                  if (!value || value === "-") return "-";
                  const url = value.startsWith("http") ? value : `https://${value}`;
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={value}
                      style={{
                        display: "inline-flex",
                        color: "#3f9f42",
                        textDecoration: "none",
                        cursor: "pointer",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <WebsiteGlobeIcon />
                    </a>
                  );
                },
                linkedin_url: (value: any) => {
                  if (!value || value === "-" || value.toLowerCase() === "linkedin.com") {
                    return "-";
                  }
                  const url = value.startsWith("http") ? value : `https://${value}`;
                  return (
                    <a
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={value}
                      style={{
                        color: "#0077b5",
                        textDecoration: "none",
                        cursor: "pointer",
                        fontSize: "16px",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
                      </svg>
                    </a>
                  );
                },
                email: (value: any) => {
                  if (!value || value === "-") return "-";
                  return (
                    <a
                      href={`mailto:${value}`}
                      style={{
                        color: "#3f9f42",
                        textDecoration: "underline",
                      }}
                      onClick={(event) => event.stopPropagation()}
                    >
                      {value}
                    </a>
                  );
                },
                notes: (value: any) => {
                  if (!value || value === "-" || value.trim() === "") return "-";
                  return (
                    <span
                      title={value}
                      style={{
                        cursor: "pointer",
                        fontSize: "16px",
                        color: "#666",
                      }}
                    >
                      Note
                    </span>
                  );
                },
              }}
              searchFields={[
                "first_name",
                "last_name",
                "full_name",
                "email",
                "company_name",
                "job_title",
                "country_or_address",
              ]}
              primaryKey="id"
              viewMode="detail"
              detailTitle={`${selectedView?.name} (#${selectedView?.id})`}
              detailDescription={
                selectedView?.description || "No description available"
              }
              onBack={() => {
                setViewMode("list");
                onViewModeChange?.("list");
                setSelectedView(null);
                setBaseViewContacts([]);
                setViewContacts([]);
                setViewMetaMissing(false);
                setSelectedContacts(new Set());
                clearViewState(clientId);
              }}
              backLabel="Back to views"
              onRefresh={() => {
                if (selectedView) {
                  return fetchContactsForView(selectedView);
                }
              }}
              isRefreshing={isLoadingViewContacts}
              columnNameMap={viewColumnNameMap}
              customHeader={detailHeader}
            />
          </div>
        )}
      </div>

      <ValidationRunPanel
        isOpen={showValidateViewContactsPanel}
        isAdmin={isAdmin}
        onClose={() => dispatch(closePanel())}
        clientId={String(clientId)}
        contactIds={validationContactIds}
        onJobChange={setActiveValidationJob}
        onCompleted={(job) => {
          if (job.status !== "failed" && job.processedCount > 0) {
            if (selectedView) {
              setValidationColumnToReveal({
                key: getValidationScoreColumn(job.checkType),
                requestId: job.id,
                scopeId: selectedView.id,
              });
            }
          }

          // Pull the new scores into the grid. The selection is deliberately
          // kept: a user who has just validated fifty contacts often wants to
          // run a second check over the same fifty.
          if (selectedView) {
            fetchContactsForView(selectedView);
          }
        }}
        onShowMessage={showContactMessage}
      />

      <BulkUpdatePanel
        //isOpen={showBulkUpdatePanel}
        isOpen={showBulkUpdatePanelModal}
        onClose={() => 
          //setShowBulkUpdatePanel(false)
          dispatch(closePanel())
        }
        selectedContactIds={Array.from(selectedContacts)}
        clientId={String(clientId)}
        onUpdateComplete={() => {
          setSelectedContacts(new Set());
          if (selectedView) {
            fetchContactsForView(selectedView);
          }
        }}
      />

<SegmentModal
  //isOpen={showSaveSegmentModal}
  isOpen={showSaveSegmentCommonModal}
  onClose={() => 
    //setShowSaveSegmentModal(false)
    dispatch(closePanel())
  }

  getContactIds={() =>
    Array.from(selectedContacts).map(id => Number(id))
  }

  selectedContactsCount={selectedContacts.size}

effectiveUserId={String(clientId)}

  onSuccess={(message) => {
    appModal.showSuccess(message);
    setSelectedContacts(new Set());
    //setShowSaveSegmentModal(false);
    dispatch(closePanel());
  }}

  onError={(message: string) => {
    console.error("Segment save error:", message);
    appModal.showError(message);
  }}

  onContactsCleared={() => {
    setSelectedContacts(new Set());
  }}
/>

<ToastMessage
  show={toast.show}
  message={toast.message}
  type={toast.type}
  onClose={hideToast}
  duration={3}
  position="bottom-center"
/>

<AppModal
  isOpen={appModal.isOpen}
  onClose={appModal.hideModal}
  {...appModal.config}
/>
      
    </div>

    
  );
  
};

export default ContactViews;
